import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Search, Download, Trash2, Edit, Upload, FileSpreadsheet,
  CheckCircle2, FileSignature, Receipt, Hash, Eye, Paperclip, X,
  TrendingUp, FileText, Banknote, Folder, AlertCircle,
  CalendarClock, Wand2, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG = {
  bozza:            { label: 'Bozza',                   color: 'bg-slate-100 text-slate-700 border-slate-300' },
  firmato_tecnico:  { label: 'Firmato Tecnico',         color: 'bg-blue-50 text-blue-700 border-blue-300' },
  controfirmato:   { label: 'Controfirmato (2 firme)', color: 'bg-indigo-50 text-indigo-700 border-indigo-300' },
  ses_gr_ricevuti:  { label: 'SES + GR ricevuti',       color: 'bg-amber-50 text-amber-700 border-amber-300' },
  fatturato:        { label: 'Fatturato',               color: 'bg-green-50 text-green-700 border-green-300' },
};

const EMPTY_LINE = {
  code: '', description: '', out_of_price_list: false,
  service: '', unit: 'PZ', quantity: '', unit_price: '', k_line_pct: 0,
};

const EMPTY_FORM = {
  work_order: '', purchase_order: '', description: '',
  site_id: '', project_id: '', date: new Date().toISOString().split('T')[0],
  lines: [],
  k_total_pct: 0, vat_pct: 22, vat_included: false,
  notes: '',
  technician_name: '', area_responsible_name: '',
  delivery_date_to_funzionario: '',
  source_type: 'manual', source_id: '',
};

function fmtEUR(n) { return (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR'; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '-'; }

export default function SESMatricesPage() {
  const { isRole } = useAuth();
  const canAdmin = isRole('admin', 'qhse');
  const [matrices, setMatrices] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [salReports, setSalReports] = useState([]);
  const [diaries, setDiaries] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterSite, setFilterSite] = useState('__all__');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Filtri aggiuntivi
  const [filterProject, setFilterProject] = useState('__all__');
  const [filterTechnician, setFilterTechnician] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [projects, setProjects] = useState([]);

  // Upload Excel
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadResult, setShowUploadResult] = useState(false);

  const [selectedMatrix, setSelectedMatrix] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showSignTech, setShowSignTech] = useState(false);
  const [showSignArea, setShowSignArea] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [signName, setSignName] = useState('');
  const [codesForm, setCodesForm] = useState({ ses_code: '', gr_code: '' });
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: '', invoiced_at: '' });

  const load = async () => {
    try {
      const params = {};
      if (filterSite !== '__all__') params.site_id = filterSite;
      if (filterProject !== '__all__') params.project_id = filterProject;
      if (filterTechnician) params.technician = filterTechnician;
      if (filterMonth) {
        const [y, m] = filterMonth.split('-');
        params.year = parseInt(y);
        params.month = parseInt(m);
      }
      const [mRes, sRes, sitesRes, salRes, diaryRes, plRes, prjRes] = await Promise.all([
        api.get('/ses-matrices', { params }),
        api.get('/ses-matrices/stats').catch(() => ({ data: null })),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/sal').catch(() => ({ data: [] })),
        api.get('/production-diary').catch(() => ({ data: [] })),
        api.get('/price-lists').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setMatrices(mRes.data || []);
      setStats(sRes.data);
      setSites(sitesRes.data || []);
      setSalReports(salRes.data || []);
      setDiaries(diaryRes.data || []);
      setPriceItems(plRes.data || []);
      setProjects(prjRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterSite, filterProject, filterTechnician, filterMonth]);

  // === HANDLERS ===
  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setAiResult(null); setAiText(''); setShowForm(true); };

  const openEdit = (m) => {
    setForm({
      work_order: m.work_order || '', purchase_order: m.purchase_order || '',
      description: m.description || '', site_id: m.site_id || '', project_id: m.project_id || '',
      date: m.date || new Date().toISOString().split('T')[0],
      lines: (m.lines || []).map(l => ({ ...l })),
      k_total_pct: m.k_total_pct || 0, vat_pct: m.vat_pct || 22, vat_included: !!m.vat_included,
      notes: m.notes || '',
      technician_name: m.technician_name || '', area_responsible_name: m.area_responsible_name || '',
      delivery_date_to_funzionario: m.delivery_date_to_funzionario ? m.delivery_date_to_funzionario.slice(0, 10) : '',
      source_type: m.source_type || 'manual', source_id: m.source_id || '',
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, value) => setForm(f => ({
    ...f,
    lines: f.lines.map((l, idx) => idx === i ? { ...l, [field]: value } : l),
  }));

  const pickPriceItem = (i, priceItemId) => {
    const p = priceItems.find(x => x.id === priceItemId);
    if (!p) return;
    setForm(f => ({
      ...f,
      lines: f.lines.map((l, idx) => idx === i ? {
        ...l,
        code: p.code || '',
        description: p.description || '',
        unit: p.unit || l.unit,
        unit_price: p.price_total || p.price_supply + p.price_work || 0,
        out_of_price_list: false,
      } : l),
    }));
  };

  // === Assistente AI codici + accantonamento + prezziario ===
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiSession, setAiSession] = useState(null);
  const runAssistant = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.post('/sarpom/assistant/suggest', { text: aiText, session_id: aiSession });
      setAiResult(res.data); setAiSession(res.data.session_id);
    } catch (e) { alert(e.response?.data?.detail || 'Errore assistente AI'); }
    finally { setAiLoading(false); }
  };
  const addSuggestionToMatrix = (s) => {
    setForm(f => ({ ...f, lines: [...f.lines, {
      ...EMPTY_LINE,
      code: s.code || '', description: s.description || '', unit: s.um || 'PZ',
      quantity: s.suggested_quantity != null ? String(s.suggested_quantity) : '',
      unit_price: s.price != null ? s.price : '', out_of_price_list: !s.in_catalog,
    }] }));
  };
  const handleCarryForward = async (m) => {
    if (!window.confirm(`Accantonare la matrice ${m.work_order || ''} al mese successivo?`)) return;
    try { await api.post(`/ses-matrices/${m.id}/carry-forward`); load(); }
    catch (e) { alert('Errore accantonamento'); }
  };
  const handleCarryForwardMonth = async () => {
    if (!filterMonth) { alert('Seleziona prima un mese'); return; }
    const [y, mo] = filterMonth.split('-');
    if (!window.confirm(`Accantonare TUTTE le matrici non fatturate di ${filterMonth} al mese successivo?`)) return;
    try {
      const r = await api.post('/ses-matrices/carry-forward-month', { year: parseInt(y), month: parseInt(mo) });
      alert(`Accantonate ${r.data.moved} matrici al mese successivo.`); load();
    } catch (e) { alert('Errore accantonamento'); }
  };
  const reloadPrezziario = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/sarpom/prezziario/reload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); alert(`Prezziario aggiornato: ${r.data.imported} codici.`); }
    catch (e) { alert(e.response?.data?.detail || 'Errore aggiornamento prezziario'); }
  };
  const fillTemplate = (m) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = '.xls,.xlsx';
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return;
      const fd = new FormData(); fd.append('file', f);
      try {
        const res = await api.post(`/ses-matrices/${m.id}/fill-template`, fd, { responseType: 'blob', headers: { 'Content-Type': 'multipart/form-data' } });
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a'); a.href = url; a.download = `MATRICE_JC_${(m.work_order || m.id.slice(0, 8)).replace(/\s/g, '_')}.xls`; a.click();
        URL.revokeObjectURL(url);
      } catch (e) { alert('Errore compilazione template ufficiale'); }
    };
    inp.click();
  };

  const computeLineTotal = (l) => {
    const q = parseFloat(l.quantity) || 0;
    const p = parseFloat(l.unit_price) || 0;
    const k = parseFloat(l.k_line_pct) || 0;
    const base = q * p;
    return base + (base * k / 100);
  };

  const previewTotals = () => {
    const baseLines = form.lines.reduce((s, l) => s + computeLineTotal(l), 0);
    const kTot = baseLines * (parseFloat(form.k_total_pct) || 0) / 100;
    const subtotal = baseLines + kTot;
    const vat = parseFloat(form.vat_pct) || 22;
    let net, vatAmt, total;
    if (form.vat_included) {
      total = subtotal;
      net = subtotal / (1 + vat / 100);
      vatAmt = subtotal - net;
    } else {
      net = subtotal;
      vatAmt = subtotal * vat / 100;
      total = subtotal + vatAmt;
    }
    return { baseLines, kTot, net, vatAmt, total };
  };

  const handleSave = async () => {
    if (!form.work_order && !form.description) { alert('Compila almeno Work Order o Descrizione'); return; }
    try {
      if (editingId) {
        await api.put(`/ses-matrices/${editingId}`, form);
      } else {
        await api.post('/ses-matrices', form);
      }
      setShowForm(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore salvataggio'); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare la matrice WO ${m.work_order || m.id.slice(0, 8)}?`)) return;
    try { await api.delete(`/ses-matrices/${m.id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openDetail = async (m) => {
    const res = await api.get(`/ses-matrices/${m.id}`);
    setSelectedMatrix(res.data);
    setShowDetail(true);
  };

  const refreshSelected = async () => {
    if (!selectedMatrix) return;
    const res = await api.get(`/ses-matrices/${selectedMatrix.id}`);
    setSelectedMatrix(res.data);
    load();
  };

  const handleSignTech = async () => {
    if (!signName.trim()) { alert('Inserisci nome funzionario tecnico'); return; }
    try {
      await api.post(`/ses-matrices/${selectedMatrix.id}/sign-technician`, { technician_name: signName });
      setShowSignTech(false); setSignName(''); refreshSelected();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleSignArea = async () => {
    if (!signName.trim()) { alert('Inserisci nome responsabile area'); return; }
    try {
      await api.post(`/ses-matrices/${selectedMatrix.id}/sign-area-responsible`, { area_responsible_name: signName });
      setShowSignArea(false); setSignName(''); refreshSelected();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleSetCodes = async () => {
    if (!codesForm.ses_code || !codesForm.gr_code) { alert('Compila entrambi SES e GR'); return; }
    try {
      await api.post(`/ses-matrices/${selectedMatrix.id}/set-codes`, codesForm);
      setShowCodes(false); setCodesForm({ ses_code: '', gr_code: '' }); refreshSelected();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleLinkInvoice = async () => {
    if (!invoiceForm.invoice_number) { alert('Inserisci numero fattura'); return; }
    try {
      await api.post(`/ses-matrices/${selectedMatrix.id}/link-invoice`, invoiceForm);
      setShowInvoice(false); setInvoiceForm({ invoice_number: '', invoiced_at: '' }); refreshSelected();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDownloadExcel = async (m) => {
    try {
      const res = await api.get(`/ses-matrices/${m.id}/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Matrice_SES_${m.work_order || m.id.slice(0, 8)}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { alert('Errore download'); }
  };

  const handleUploadExcelMatrix = async (file) => {
    if (!file) return;
    setUploadingExcel(true);
    setUploadResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/ses-matrices/upload-excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
      setShowUploadResult(true);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload Excel');
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleDownloadOriginal = (m) => {
    if (!m.original_file_url) return alert('Nessun file originale');
    const url = `${process.env.REACT_APP_BACKEND_URL}${m.original_file_url}`;
    window.open(url, '_blank');
  };

  const handleReparseAll = async () => {
    if (!window.confirm('Ri-elaborare tutte le matrici caricate da Excel? Estraera nuovamente funzionario, totale temporaneo, prezzi.')) return;
    try {
      const res = await api.post('/ses-matrices/reparse-all');
      alert(`Aggiornate ${res.data.updated} / ${res.data.total} matrici.`);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleReparseOne = async (m) => {
    try {
      await api.post(`/ses-matrices/${m.id}/reparse`);
      load();
      if (selectedMatrix?.id === m.id) {
        const fresh = await api.get(`/ses-matrices/${m.id}`);
        setSelectedMatrix(fresh.data);
      }
    } catch (err) { alert(err.response?.data?.detail || 'Errore re-parse'); }
  };

  const handleUploadAttachment = async (file) => {
    if (!selectedMatrix || !file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/ses-matrices/${selectedMatrix.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      refreshSelected();
    } catch (err) { alert(err.response?.data?.detail || 'Errore upload'); }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Eliminare allegato?')) return;
    try { await api.delete(`/ses-matrices/${selectedMatrix.id}/attachments/${attId}`); refreshSelected(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const filtered = matrices.filter(m => {
    if (filterStatus !== '__all__' && m.status !== filterStatus) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.work_order || '').toLowerCase().includes(s)
      || (m.purchase_order || '').toLowerCase().includes(s)
      || (m.description || '').toLowerCase().includes(s)
      || (m.ses_code || '').toLowerCase().includes(s)
      || (m.gr_code || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  const totals = previewTotals();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ses-matrices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Matrici SES (Sarpom)</h1>
        <div className="flex flex-wrap gap-2">
          {canAdmin && <Button variant="outline" size="sm" onClick={handleReparseAll} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="reparse-all-btn" title="Ri-elabora tutte le matrici caricate (estrae funzionario, totale temp, prezzi)">
            <FileSpreadsheet size={14} className="mr-1" /> Aggiorna Campi
          </Button>}
          <label className="cursor-pointer">
            <input type="file" className="hidden" accept=".xls,.xlsx" onChange={e => e.target.files?.[0] && handleUploadExcelMatrix(e.target.files[0])} data-testid="upload-excel-input" disabled={uploadingExcel} />
            <span className={`inline-flex items-center text-sm px-3 py-2 rounded-md border ${uploadingExcel ? 'opacity-60' : ''} border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer`}>
              <Upload size={14} className="mr-1.5" /> {uploadingExcel ? 'Caricamento...' : 'Carica Excel'}
            </span>
          </label>
          <Button onClick={openCreate} data-testid="new-matrix-btn"><Plus size={16} className="mr-1" /> Nuova Matrice</Button>
        </div>
      </div>

      {/* STATS */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(stats.counts || {}).map(([k, v]) => (
              <Card key={k} className={`border ${STATUS_CONFIG[k]?.color || ''} cursor-pointer hover:shadow-md`} onClick={() => setFilterStatus(k)} data-testid={`stat-${k}`}>
                <CardContent className="p-3 text-center">
                  <p className="text-xs font-medium">{STATUS_CONFIG[k]?.label || k}</p>
                  <p className="text-2xl font-bold">{v}</p>
                </CardContent>
              </Card>
            ))}
            <Card className="border bg-emerald-50 border-emerald-200">
              <CardContent className="p-3 text-center">
                <TrendingUp size={14} className="mx-auto text-emerald-700 mb-0.5" />
                <p className="text-xs font-medium text-emerald-800">Previsione Incasso</p>
                <p className="text-lg font-bold text-emerald-900">{fmtEUR(stats.revenue_forecast)}</p>
              </CardContent>
            </Card>
          </div>

          {/* PIPELINE FATTURAZIONE */}
          {stats.pipeline && (
            <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="pipeline-card">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-emerald-50 py-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
                  <Banknote size={16} className="text-indigo-600" /> Pipeline Fatturazione Anticipata
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('bozza')} data-testid="pipe-in-compilazione">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">In Compilazione</span>
                      <FileSpreadsheet size={14} className="text-slate-500" />
                    </div>
                    <p className="text-lg font-bold text-slate-900">{fmtEUR(stats.pipeline.in_compilazione)}</p>
                    <p className="text-xs text-slate-500 mt-1">Bozze + Firmato tecnico</p>
                  </div>
                  <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('controfirmato')} data-testid="pipe-pronti-sarpom">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-indigo-700">Pronti per Sarpom</span>
                      <Upload size={14} className="text-indigo-600" />
                    </div>
                    <p className="text-lg font-bold text-indigo-900">{fmtEUR(stats.pipeline.pronti_per_sarpom)}</p>
                    <p className="text-xs text-indigo-600 mt-1">Da caricare sul portale</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('ses_gr_ricevuti')} data-testid="pipe-attesa-fattura">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-amber-700">In Attesa Fattura</span>
                      <Hash size={14} className="text-amber-600" />
                    </div>
                    <p className="text-lg font-bold text-amber-900">{fmtEUR(stats.pipeline.in_attesa_fattura)}</p>
                    <p className="text-xs text-amber-600 mt-1">SES + GR ricevuti, da fatturare</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('fatturato')} data-testid="pipe-fatturati">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-green-700">Fatturati</span>
                      <Receipt size={14} className="text-green-600" />
                    </div>
                    <p className="text-lg font-bold text-green-900">{fmtEUR(stats.pipeline.fatturati)}</p>
                    <p className="text-xs text-green-600 mt-1">Gia' fatturati al cliente</p>
                  </div>
                </div>
                {/* Visual progress bar */}
                {(() => {
                  const totalPipeline = (stats.pipeline.in_compilazione || 0) + (stats.pipeline.pronti_per_sarpom || 0) + (stats.pipeline.in_attesa_fattura || 0) + (stats.pipeline.fatturati || 0);
                  if (totalPipeline === 0) return <p className="text-xs text-slate-400 text-center">Nessuna matrice in pipeline</p>;
                  const pct = (v) => Math.max(0, Math.min(100, (v / totalPipeline) * 100));
                  return (
                    <div className="flex h-3 rounded-full overflow-hidden border border-slate-200" title={`Totale ${fmtEUR(totalPipeline)}`}>
                      <div className="bg-slate-400" style={{ width: `${pct(stats.pipeline.in_compilazione)}%` }} title={`In Compilazione: ${fmtEUR(stats.pipeline.in_compilazione)}`} />
                      <div className="bg-indigo-500" style={{ width: `${pct(stats.pipeline.pronti_per_sarpom)}%` }} title={`Pronti Sarpom: ${fmtEUR(stats.pipeline.pronti_per_sarpom)}`} />
                      <div className="bg-amber-500" style={{ width: `${pct(stats.pipeline.in_attesa_fattura)}%` }} title={`In Attesa Fattura: ${fmtEUR(stats.pipeline.in_attesa_fattura)}`} />
                      <div className="bg-green-600" style={{ width: `${pct(stats.pipeline.fatturati)}%` }} title={`Fatturati: ${fmtEUR(stats.pipeline.fatturati)}`} />
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between mt-3 text-xs text-slate-600 pt-2 border-t">
                  <span>Totale in pipeline (non ancora fatturato): <strong className="text-slate-900">{fmtEUR((stats.pipeline.in_compilazione || 0) + (stats.pipeline.pronti_per_sarpom || 0) + (stats.pipeline.in_attesa_fattura || 0))}</strong></span>
                  <span>Gia' fatturato: <strong className="text-green-700">{fmtEUR(stats.revenue_invoiced)}</strong></span>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* FILTERS */}
      <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input className="pl-9" placeholder="Cerca WO, PO, SES, GR, descrizione..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="lg:w-44"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="lg:w-44" data-testid="filter-project"><SelectValue placeholder="Progetto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti i progetti</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSite} onValueChange={setFilterSite}>
          <SelectTrigger className="lg:w-44"><SelectValue placeholder="Cantiere" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti i cantieri</SelectItem>
            {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input className="lg:w-40" placeholder="Funzionario" value={filterTechnician} onChange={e => setFilterTechnician(e.target.value)} data-testid="filter-technician" />
        <Input type="month" className="lg:w-40" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} data-testid="filter-month" />
        {(filterProject !== '__all__' || filterSite !== '__all__' || filterTechnician || filterMonth || filterStatus !== '__all__') && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterProject('__all__'); setFilterSite('__all__'); setFilterTechnician(''); setFilterMonth(''); setFilterStatus('__all__'); }} className="text-xs">
            <X size={12} className="mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* AZIONI MESE + PREZZIARIO */}
      {canAdmin && (
        <div className="flex flex-wrap items-center gap-2 -mt-1">
          {filterMonth && (
            <Button variant="outline" size="sm" onClick={handleCarryForwardMonth} data-testid="carry-forward-month-btn">
              <CalendarClock size={14} className="mr-1 text-amber-600" /> Accantona tutte al mese successivo
            </Button>
          )}
          <label className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer hover:bg-slate-50" data-testid="reload-prezziario-label">
            <Upload size={14} className="text-slate-500" /> Aggiorna prezziario (Excel)
            <input type="file" accept=".xls,.xlsx" className="hidden" onChange={e => reloadPrezziario(e.target.files?.[0])} data-testid="reload-prezziario-input" />
          </label>
        </div>
      )}

      {/* TABLE */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>WO</TableHead>
                <TableHead className="hidden sm:table-cell">Descrizione / Funzionario</TableHead>
                <TableHead>Cantiere / Area</TableHead>
                <TableHead className="hidden md:table-cell">SES / GR</TableHead>
                <TableHead className="hidden lg:table-cell">Fattura</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-32">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Nessuna matrice trovata</TableCell></TableRow>
              ) : filtered.map(m => {
                const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.bozza;
                const tempAmt = m.temp_total || 0;
                const showTemp = m.status === 'bozza' && tempAmt > 0;
                return (
                  <TableRow key={m.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(m)} data-testid={`matrix-row-${m.id}`}>
                    <TableCell className="text-sm">{fmtDate(m.date)}</TableCell>
                    <TableCell className="font-mono text-xs">{m.work_order || '-'}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="text-sm font-medium text-slate-900 max-w-[200px] truncate" title={m.description}>{m.description || '-'}</div>
                      {m.funzionario_code && (
                        <Badge variant="outline" className="mt-0.5 bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-mono">
                          <FileSignature size={9} className="mr-0.5" /> {m.funzionario_code}
                        </Badge>
                      )}
                      {m.delivery_date_to_funzionario && (
                        <Badge variant="outline" className="ml-1 mt-0.5 bg-cyan-50 text-cyan-700 border-cyan-200 text-[10px]">
                          <Upload size={9} className="mr-0.5" /> Consegnato {new Date(m.delivery_date_to_funzionario).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {m.site_name || m.project_name || '-'}
                      {m.funzionario_nome && <div className="text-xs text-slate-500 mt-0.5">{m.funzionario_nome}{m.area_funzionario ? ` (${m.area_funzionario})` : ''}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {m.ses_code || m.gr_code ? (
                        <div className="flex flex-col gap-0.5 text-xs">
                          {m.ses_code && <span className="font-mono">SES: <strong>{m.ses_code}</strong></span>}
                          {m.gr_code && <span className="font-mono">GR: <strong>{m.gr_code}</strong></span>}
                        </div>
                      ) : <span className="text-xs text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">{m.invoice_number || <span className="text-slate-400">-</span>}</TableCell>
                    <TableCell className="text-right text-sm">
                      {showTemp ? (
                        <div>
                          <div className="text-amber-700 font-semibold" title="Totale temporaneo dal file Excel (pre-firma)">{fmtEUR(tempAmt)}</div>
                          <div className="text-[10px] text-amber-600 italic">temp.</div>
                        </div>
                      ) : (
                        <div className="font-bold text-slate-900">{fmtEUR(m.total_amount)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cfg.color}>{cfg.label}</Badge>
                      {m.source_type === 'uploaded_excel' && <Badge variant="outline" className="ml-1 bg-purple-50 text-purple-700 border-purple-200 text-[10px]">XLS</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => openDetail(m)} title="Dettaglio"><Eye size={14} /></Button>
                        {m.original_file_url && (
                          <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleDownloadOriginal(m)} title="Scarica file originale">
                            <FileText size={14} className="text-purple-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleDownloadExcel(m)} title="Scarica Excel generato"><Download size={14} className="text-green-600" /></Button>
                        <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => fillTemplate(m)} title="Compila template ufficiale JC (carica il tuo Excel)" data-testid={`fill-template-${m.id}`}><FileSpreadsheet size={14} className="text-indigo-600" /></Button>
                        <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => openEdit(m)} title="Modifica"><Edit size={14} /></Button>
                        {canAdmin && m.status !== 'fatturato' && <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleCarryForward(m)} title="Accantona al mese successivo" data-testid={`carry-forward-${m.id}`}><CalendarClock size={14} className="text-amber-600" /></Button>}
                        {canAdmin && <Button variant="ghost" size="sm" className="p-1 h-7 w-7" onClick={() => handleDelete(m)} title="Elimina"><Trash2 size={14} className="text-red-600" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Matrice SES' : 'Nuova Matrice SES'}</DialogTitle>
            <DialogDescription>Compila i campi della matrice. Puoi anche generarla da un SAL o Diario.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Source picker */}
            {!editingId && (
              <Card className="border-indigo-200 bg-indigo-50/50">
                <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Origine</Label>
                    <Select value={form.source_type} onValueChange={v => setForm(f => ({ ...f, source_type: v, source_id: '' }))}>
                      <SelectTrigger className="h-9" data-testid="source-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manuale (da zero)</SelectItem>
                        <SelectItem value="sal">Da SAL</SelectItem>
                        <SelectItem value="diary">Da Diario di Produzione</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.source_type === 'sal' && (
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Seleziona SAL</Label>
                      <Select value={form.source_id} onValueChange={v => setForm(f => ({ ...f, source_id: v }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>{salReports.map(s => <SelectItem key={s.id} value={s.id}>{s.report_number || s.id.slice(0, 8)} - {s.month}/{s.year}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.source_type === 'diary' && (
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Seleziona Diario</Label>
                      <Select value={form.source_id} onValueChange={v => setForm(f => ({ ...f, source_id: v }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>{diaries.slice(0, 50).map(d => <SelectItem key={d.id} value={d.id}>{d.date} - PDL {d.numero_pdl || '-'}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Header fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Work Order *</Label><Input value={form.work_order} onChange={e => setForm(f => ({ ...f, work_order: e.target.value }))} data-testid="wo-input" /></div>
              <div className="space-y-1.5"><Label>Purchase Order</Label><Input value={form.purchase_order} onChange={e => setForm(f => ({ ...f, purchase_order: e.target.value }))} data-testid="po-input" /></div>
              <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
              <div className="space-y-1.5 md:col-span-2"><Label>Descrizione</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Cantiere</Label>
                <Select value={form.site_id || '__none__'} onValueChange={v => setForm(f => ({ ...f, site_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Progetto</Label>
                <Select value={form.project_id || '__none__'} onValueChange={v => setForm(f => ({ ...f, project_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="project-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Funzionario Tecnico</Label>
                <Input value={form.technician_name} onChange={e => setForm(f => ({ ...f, technician_name: e.target.value }))} placeholder="Es. Mario Rossi" data-testid="technician-input" />
              </div>
              <div className="space-y-1.5"><Label>Data Consegna al Funzionario</Label>
                <Input type="date" value={form.delivery_date_to_funzionario} onChange={e => setForm(f => ({ ...f, delivery_date_to_funzionario: e.target.value }))} placeholder="Quando hai consegnato il foglio" data-testid="delivery-date-input" />
              </div>
            </div>

            {/* Assistente AI codici */}
            <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/40 space-y-2" data-testid="ai-assistant-panel">
              <div className="flex items-center gap-2"><Wand2 size={16} className="text-indigo-600" /><Label className="font-semibold text-indigo-900">Assistente AI — trova i codici</Label></div>
              <p className="text-xs text-slate-500">Descrivi il lavoro svolto nella giornata: ti suggerisco i codici del prezziario da mettere in matrice. Premi "Aggiungi" per inserire la riga.</p>
              <Textarea rows={2} value={aiText} onChange={e => setAiText(e.target.value)} placeholder="Es. Sabbiatura a metallo quasi bianco di tubazione per 120 mq, applicato primer epossidico, 3 ore ispettore..." data-testid="ai-input" />
              <div className="flex justify-end">
                <Button size="sm" onClick={runAssistant} disabled={aiLoading || !aiText.trim()} data-testid="ai-suggest-btn">
                  {aiLoading ? <><Loader2 size={14} className="mr-1 animate-spin" /> Analizzo...</> : <><Wand2 size={14} className="mr-1" /> Suggerisci codici</>}
                </Button>
              </div>
              {aiResult && (
                <div className="space-y-2" data-testid="ai-results">
                  {aiResult.note && <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">{aiResult.note}</p>}
                  {aiResult.suggestions.length === 0 && <p className="text-xs text-slate-500">Nessun codice trovato. Aggiungi dettagli (superficie, altezza, tipo di prodotto, ore).</p>}
                  {aiResult.suggestions.map((s, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-2 bg-white border rounded p-2" data-testid={`ai-suggestion-${idx}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-bold">{s.code}</span>
                          <Badge variant="outline" className={s.match === 'esatto' ? 'bg-green-50 text-green-700 border-green-200 text-[10px]' : s.match === 'probabile' ? 'bg-blue-50 text-blue-700 border-blue-200 text-[10px]' : 'bg-amber-50 text-amber-700 border-amber-200 text-[10px]'}>{s.match}</Badge>
                          <span className="text-xs text-slate-500">UM {s.um} · {fmtEUR(s.price)}{s.suggested_quantity != null ? ` · qta ${s.suggested_quantity}` : ''}</span>
                        </div>
                        <p className="text-sm text-slate-800">{s.description}</p>
                        {s.reason && <p className="text-xs text-slate-500">{s.reason}</p>}
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0" onClick={() => addSuggestionToMatrix(s)} data-testid={`ai-add-${idx}`}><Plus size={13} className="mr-1" /> Aggiungi</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lines */}
            <div className="border rounded-lg p-3 space-y-2 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Righe ({form.lines.length})</Label>
                <Button size="sm" onClick={addLine} data-testid="add-line-btn"><Plus size={14} className="mr-1" /> Aggiungi Riga</Button>
              </div>
              {form.lines.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-3">Nessuna riga. Aggiungi voci con il pulsante sopra.</p>
              ) : (
                <div className="space-y-2 overflow-x-auto">
                  {form.lines.map((l, i) => (
                    <div key={i} className="p-2 bg-white rounded border space-y-2" data-testid={`line-${i}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-2">
                          <Label className="text-xs">Codice/Voce prezziario</Label>
                          <div className="flex gap-1">
                            <Input className="h-8 text-xs" value={l.code} onChange={e => updateLine(i, 'code', e.target.value)} placeholder="es. A-001" />
                            <Select value="" onValueChange={(v) => pickPriceItem(i, v)}>
                              <SelectTrigger className="h-8 w-10 p-1"><Folder size={12} /></SelectTrigger>
                              <SelectContent>
                                {priceItems.slice(0, 100).filter(p => p.price_type !== 'percentage_increase' && p.price_type !== 'percentage_discount').map(p => (
                                  <SelectItem key={p.id} value={p.id}>{p.code} - {p.description?.slice(0, 30)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <Label className="text-xs">Descrizione</Label>
                          <Input className="h-8 text-xs" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                        </div>
                        <div className="col-span-6 sm:col-span-2">
                          <Label className="text-xs">Servizio</Label>
                          <Input className="h-8 text-xs" value={l.service} onChange={e => updateLine(i, 'service', e.target.value)} />
                        </div>
                        <div className="col-span-3 sm:col-span-1">
                          <Label className="text-xs">U.M.</Label>
                          <Input className="h-8 text-xs" value={l.unit} onChange={e => updateLine(i, 'unit', e.target.value)} />
                        </div>
                        <div className="col-span-3 sm:col-span-1">
                          <Label className="text-xs">Qta</Label>
                          <Input className="h-8 text-xs" type="number" step="0.01" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                        </div>
                        <div className="col-span-4 sm:col-span-1">
                          <Label className="text-xs">Prezzo</Label>
                          <Input className="h-8 text-xs" type="number" step="0.01" value={l.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} />
                        </div>
                        <div className="col-span-4 sm:col-span-1">
                          <Label className="text-xs">K %</Label>
                          <Input className="h-8 text-xs" type="number" step="0.1" value={l.k_line_pct} onChange={e => updateLine(i, 'k_line_pct', e.target.value)} title="Aumento/sconto per la riga (%)" />
                        </div>
                        <div className="col-span-4 sm:col-span-1 flex flex-col items-end">
                          <span className="text-xs text-slate-500">Totale</span>
                          <span className="text-sm font-bold text-green-700">{fmtEUR(computeLineTotal(l))}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-1.5 text-xs text-amber-700">
                          <Checkbox checked={l.out_of_price_list} onCheckedChange={v => updateLine(i, 'out_of_price_list', v)} />
                          Fuori prezziario
                        </label>
                        <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => removeLine(i)}>
                          <Trash2 size={13} className="mr-1" /> Rimuovi
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals & K total & VAT */}
            <Card className="border-slate-200">
              <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">K Totale Matrice %</Label>
                  <Input type="number" step="0.1" value={form.k_total_pct} onChange={e => setForm(f => ({ ...f, k_total_pct: e.target.value }))} title="Aumento/sconto totale sulla matrice" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">IVA %</Label>
                  <Input type="number" step="0.1" value={form.vat_pct} onChange={e => setForm(f => ({ ...f, vat_pct: e.target.value }))} />
                </div>
                <div className="space-y-1.5 flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.vat_included} onCheckedChange={v => setForm(f => ({ ...f, vat_included: v }))} data-testid="vat-included" />
                    IVA gia' inclusa nei prezzi
                  </label>
                </div>
                <div className="space-y-1 text-right text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotale righe:</span><span className="font-medium">{fmtEUR(totals.baseLines)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">K totale:</span><span className="font-medium">{fmtEUR(totals.kTot)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Imponibile:</span><span className="font-medium">{fmtEUR(totals.net)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">IVA:</span><span className="font-medium">{fmtEUR(totals.vatAmt)}</span></div>
                  <div className="flex justify-between border-t pt-1 mt-1"><span className="font-bold">TOTALE:</span><span className="text-green-700 font-bold">{fmtEUR(totals.total)}</span></div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="save-matrix-btn"><FileSpreadsheet size={14} className="mr-1" /> {editingId ? 'Salva Modifiche' : 'Crea Matrice'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedMatrix && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Matrice SES - {selectedMatrix.work_order || selectedMatrix.id.slice(0, 8)}
                  <Badge variant="outline" className={STATUS_CONFIG[selectedMatrix.status]?.color}>{STATUS_CONFIG[selectedMatrix.status]?.label}</Badge>
                </DialogTitle>
              </DialogHeader>

              {/* Workflow buttons */}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => { setSignName(selectedMatrix.technician_name || ''); setShowSignTech(true); }} disabled={!!selectedMatrix.technician_signed_at} data-testid="sign-tech-btn">
                  <FileSignature size={14} className="mr-1" /> {selectedMatrix.technician_signed_at ? 'Firmato Tecnico' : 'Firma Tecnico'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setSignName(selectedMatrix.area_responsible_name || ''); setShowSignArea(true); }} disabled={!selectedMatrix.technician_signed_at || !!selectedMatrix.area_responsible_signed_at} data-testid="sign-area-btn">
                  <CheckCircle2 size={14} className="mr-1" /> {selectedMatrix.area_responsible_signed_at ? 'Controfirmato' : 'Controfirma Area'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setCodesForm({ ses_code: selectedMatrix.ses_code || '', gr_code: selectedMatrix.gr_code || '' }); setShowCodes(true); }} disabled={!selectedMatrix.area_responsible_signed_at} data-testid="set-codes-btn">
                  <Hash size={14} className="mr-1" /> {selectedMatrix.ses_code ? `SES: ${selectedMatrix.ses_code} / GR: ${selectedMatrix.gr_code}` : 'Inserisci SES/GR'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setInvoiceForm({ invoice_number: selectedMatrix.invoice_number || '', invoiced_at: selectedMatrix.invoiced_at?.slice(0, 10) || '' }); setShowInvoice(true); }} disabled={!selectedMatrix.ses_code} data-testid="link-invoice-btn">
                  <Receipt size={14} className="mr-1" /> {selectedMatrix.invoice_number ? `Fatt: ${selectedMatrix.invoice_number}` : 'Collega Fattura'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDownloadExcel(selectedMatrix)} className="border-green-300 text-green-700"><Download size={14} className="mr-1" /> Excel</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selectedMatrix); }}><Edit size={14} className="mr-1" /> Modifica</Button>
              </div>

              {/* Summary */}
              <Card className="border-slate-200">
                <CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-slate-500">Work Order</p><p className="font-medium">{selectedMatrix.work_order || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Purchase Order</p><p className="font-medium">{selectedMatrix.purchase_order || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Data</p><p className="font-medium">{fmtDate(selectedMatrix.date)}</p></div>
                  <div><p className="text-xs text-slate-500">Cantiere/Progetto</p><p className="font-medium">{selectedMatrix.site_name || selectedMatrix.project_name || '-'}</p></div>
                  {(selectedMatrix.sarpom_contract || selectedMatrix.sarpom_item) && (
                    <>
                      <div><p className="text-xs text-slate-500">Contratto Sarpom</p><p className="font-mono text-xs">{selectedMatrix.sarpom_contract || '-'}</p></div>
                      <div><p className="text-xs text-slate-500">Item Contratto</p><p className="font-mono text-xs">{selectedMatrix.sarpom_item || '-'}</p></div>
                    </>
                  )}
                  {(selectedMatrix.funzionario_code || selectedMatrix.funzionario_nome) && (
                    <>
                      <div><p className="text-xs text-slate-500">Funzionario (codice)</p><p className="font-mono text-sm font-bold text-blue-700">{selectedMatrix.funzionario_code || '-'}</p></div>
                      <div><p className="text-xs text-slate-500">Funzionario (nome)</p><p className="text-sm">{selectedMatrix.funzionario_nome || '-'}</p></div>
                    </>
                  )}
                  {selectedMatrix.area_funzionario && (
                    <div><p className="text-xs text-slate-500">Area Funzionario</p><p className="font-medium">{selectedMatrix.area_funzionario}</p></div>
                  )}
                  {(selectedMatrix.temp_total > 0 && selectedMatrix.status === 'bozza') && (
                    <div className="col-span-2 md:col-span-4 bg-amber-50 border border-amber-200 rounded p-2">
                      <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                        <AlertCircle size={12} /> Totale Temporaneo (pre-firma)
                      </p>
                      <p className="text-2xl font-bold text-amber-900">{fmtEUR(selectedMatrix.temp_total)}</p>
                      <p className="text-xs text-amber-600 mt-1">Questo importo viene confermato definitivamente quando si genera il SES.</p>
                    </div>
                  )}
                  {selectedMatrix.delivery_date_to_funzionario && (
                    <div className="col-span-2 md:col-span-4 bg-cyan-50 border border-cyan-200 rounded p-2 flex items-center gap-2">
                      <Upload size={16} className="text-cyan-700" />
                      <div>
                        <p className="text-xs text-cyan-700 font-medium">Foglio consegnato al funzionario il</p>
                        <p className="font-bold text-cyan-900">{fmtDate(selectedMatrix.delivery_date_to_funzionario)}</p>
                      </div>
                    </div>
                  )}
                  {selectedMatrix.original_file_url && (
                    <div>
                      <p className="text-xs text-slate-500">File originale caricato</p>
                      <Button variant="link" size="sm" className="h-auto p-0 text-purple-700" onClick={() => handleDownloadOriginal(selectedMatrix)}>
                        <FileText size={12} className="mr-1" /> {selectedMatrix.original_filename || 'Scarica'}
                      </Button>
                    </div>
                  )}
                  <div className="col-span-2 md:col-span-4"><p className="text-xs text-slate-500">Descrizione</p><p className="font-medium">{selectedMatrix.description || '-'}</p></div>
                  {(selectedMatrix.personnel_names || []).length > 0 && (
                    <div className="col-span-2 md:col-span-4">
                      <p className="text-xs text-slate-500 mb-1">Nominativi/Personale ({selectedMatrix.personnel_names.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedMatrix.personnel_names.map((n, i) => <Badge key={i} variant="outline" className="bg-slate-50 text-xs">{n}</Badge>)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Lines */}
              <Card className="border-slate-200">
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Codice</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead>U.M.</TableHead>
                        <TableHead className="text-right">Qta</TableHead>
                        <TableHead className="text-right">Prezzo</TableHead>
                        <TableHead className="text-right">K %</TableHead>
                        <TableHead className="text-right">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedMatrix.lines || []).map((l, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{l.code}{l.out_of_price_list && <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-200 text-[10px]">FP</Badge>}</TableCell>
                          <TableCell className="text-sm">{l.description}</TableCell>
                          <TableCell className="text-xs">{l.unit}</TableCell>
                          <TableCell className="text-right text-sm">{l.quantity}</TableCell>
                          <TableCell className="text-right text-sm">{fmtEUR(l.unit_price)}</TableCell>
                          <TableCell className="text-right text-sm">{l.k_line_pct || 0}%</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{fmtEUR(l.line_total || (parseFloat(l.quantity || 0) * parseFloat(l.unit_price || 0)))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Totals */}
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="p-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                  <div><p className="text-xs text-slate-500">Subtotale righe</p><p className="font-bold">{fmtEUR(selectedMatrix.subtotal_lines)}</p></div>
                  <div><p className="text-xs text-slate-500">K totale ({selectedMatrix.k_total_pct || 0}%)</p><p className="font-bold">{fmtEUR(selectedMatrix.k_total_adjustment)}</p></div>
                  <div><p className="text-xs text-slate-500">Imponibile</p><p className="font-bold">{fmtEUR(selectedMatrix.net_amount)}</p></div>
                  <div><p className="text-xs text-slate-500">IVA {selectedMatrix.vat_pct}%{selectedMatrix.vat_included ? ' (incl.)' : ''}</p><p className="font-bold">{fmtEUR(selectedMatrix.vat_amount)}</p></div>
                  <div><p className="text-xs text-slate-500">TOTALE</p><p className="font-bold text-green-700 text-lg">{fmtEUR(selectedMatrix.total_amount)}</p></div>
                </CardContent>
              </Card>

              {/* Signatures status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Card className={`border ${selectedMatrix.technician_signed_at ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200'}`}>
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Funzionario Tecnico</p>
                    <p className="font-medium">{selectedMatrix.technician_name || '(non firmato)'}</p>
                    {selectedMatrix.technician_signed_at && <p className="text-xs text-blue-700 mt-1">Firmato il {fmtDate(selectedMatrix.technician_signed_at)}</p>}
                  </CardContent>
                </Card>
                <Card className={`border ${selectedMatrix.area_responsible_signed_at ? 'border-indigo-200 bg-indigo-50/50' : 'border-slate-200'}`}>
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Responsabile Area</p>
                    <p className="font-medium">{selectedMatrix.area_responsible_name || '(non controfirmato)'}</p>
                    {selectedMatrix.area_responsible_signed_at && <p className="text-xs text-indigo-700 mt-1">Controfirmato il {fmtDate(selectedMatrix.area_responsible_signed_at)}</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Attachments */}
              <Card className="border-slate-200">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold flex items-center gap-1"><Paperclip size={14} /> Allegati ({selectedMatrix.attachments?.length || 0})</p>
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={e => e.target.files?.[0] && handleUploadAttachment(e.target.files[0])} accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png" />
                      <span className="inline-flex items-center text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer"><Upload size={12} className="mr-1" /> Carica</span>
                    </label>
                  </div>
                  {(selectedMatrix.attachments || []).length === 0 ? (
                    <p className="text-xs text-slate-400">Nessun allegato</p>
                  ) : (
                    <div className="space-y-1">
                      {selectedMatrix.attachments.map(a => (
                        <div key={a.id} className="flex items-center justify-between text-sm bg-slate-50 rounded p-1.5 px-2">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <FileText size={12} /> {a.original_name}
                          </a>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteAttachment(a.id)}>
                            <X size={12} className="text-red-600" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedMatrix.notes && (
                <Card className="border-amber-200 bg-amber-50/30"><CardContent className="p-3"><p className="text-xs text-slate-500 mb-1">Note</p><p className="text-sm">{selectedMatrix.notes}</p></CardContent></Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* SIGN DIALOGS */}
      <Dialog open={showSignTech} onOpenChange={setShowSignTech}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Firma Funzionario Tecnico</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nome Funzionario Tecnico *</Label>
            <Input value={signName} onChange={e => setSignName(e.target.value)} placeholder="Es. Mario Rossi" data-testid="sign-tech-name" />
            <p className="text-xs text-slate-500">Firma dichiarativa con timestamp automatico.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignTech(false)}>Annulla</Button>
            <Button onClick={handleSignTech} data-testid="confirm-sign-tech"><FileSignature size={14} className="mr-1" /> Firma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSignArea} onOpenChange={setShowSignArea}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Controfirma Responsabile Area</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nome Responsabile Area *</Label>
            <Input value={signName} onChange={e => setSignName(e.target.value)} placeholder="Es. Luigi Bianchi" data-testid="sign-area-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignArea(false)}>Annulla</Button>
            <Button onClick={handleSignArea} data-testid="confirm-sign-area"><CheckCircle2 size={14} className="mr-1" /> Controfirma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCodes} onOpenChange={setShowCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Codici Sarpom (SES + GR)</DialogTitle><DialogDescription>Inserisci i codici generati dal portale Sarpom dopo l'upload della matrice.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Codice SES *</Label><Input value={codesForm.ses_code} onChange={e => setCodesForm(f => ({ ...f, ses_code: e.target.value }))} placeholder="Es. SES-2026-0001" data-testid="ses-code" /></div>
            <div><Label>Codice GR *</Label><Input value={codesForm.gr_code} onChange={e => setCodesForm(f => ({ ...f, gr_code: e.target.value }))} placeholder="Es. GR-2026-0001" data-testid="gr-code" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodes(false)}>Annulla</Button>
            <Button onClick={handleSetCodes} data-testid="confirm-codes"><Hash size={14} className="mr-1" /> Salva codici</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Collega Fattura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Numero Fattura *</Label><Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="Es. 2026/001" data-testid="invoice-number" /></div>
            <div><Label>Data Fatturazione</Label><Input type="date" value={invoiceForm.invoiced_at} onChange={e => setInvoiceForm(f => ({ ...f, invoiced_at: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoice(false)}>Annulla</Button>
            <Button onClick={handleLinkInvoice} data-testid="confirm-invoice"><Receipt size={14} className="mr-1" /> Collega</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPLOAD RESULT DIALOG */}
      <Dialog open={showUploadResult} onOpenChange={setShowUploadResult}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 size={20} className="text-green-600" /> Excel Caricato</DialogTitle>
            <DialogDescription>Il file è stato analizzato e una nuova matrice è stata creata.</DialogDescription>
          </DialogHeader>
          {uploadResult && (
            <div className="space-y-3 text-sm">
              <Card className="border-purple-200 bg-purple-50/30">
                <CardContent className="p-3 grid grid-cols-2 gap-2">
                  <div><p className="text-xs text-slate-500">Work Order auto-estratto</p><p className="font-mono font-medium">{uploadResult.work_order || '(non trovato)'}</p></div>
                  <div><p className="text-xs text-slate-500">Purchase Order</p><p className="font-mono font-medium">{uploadResult.purchase_order || '(non trovato)'}</p></div>
                  <div><p className="text-xs text-slate-500">Contratto Sarpom</p><p className="font-mono">{uploadResult.sarpom_contract || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Item Contratto</p><p className="font-mono">{uploadResult.sarpom_item || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Funzionario (codice)</p><p className="font-mono font-bold text-blue-700">{uploadResult.funzionario_code || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Funzionario (nome)</p><p>{uploadResult.funzionario_nome || '-'}{uploadResult.area_funzionario ? ` (${uploadResult.area_funzionario})` : ''}</p></div>
                  <div className="col-span-2"><p className="text-xs text-slate-500">Descrizione</p><p>{uploadResult.description || '-'}</p></div>
                  <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                    <p className="text-xs text-amber-700 font-medium flex items-center gap-1"><AlertCircle size={12} /> Totale Temporaneo (pre-firma)</p>
                    <p className="text-xl font-bold text-amber-900">{fmtEUR(uploadResult.temp_total)}</p>
                  </div>
                  <div className="col-span-2"><p className="text-xs text-slate-500">Nominativi rilevati ({uploadResult.personnel_names?.length || 0})</p><div className="flex flex-wrap gap-1">{(uploadResult.personnel_names || []).map((n, i) => <Badge key={i} variant="outline" className="bg-white text-xs">{n}</Badge>)}</div></div>
                  <div className="col-span-2"><p className="text-xs text-slate-500">Righe estratte (con prezzi)</p><p className="font-medium">{uploadResult.lines?.length || 0} righe</p></div>
                </CardContent>
              </Card>
              {uploadResult._parser_warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                  <strong>Avvisi:</strong> {uploadResult._parser_warnings.join('; ')}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Il file originale è stato salvato come allegato. Puoi modificare i campi mancanti aprendo il dettaglio della matrice.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadResult(false)}>Chiudi</Button>
            <Button onClick={() => { setShowUploadResult(false); if (uploadResult) openDetail(uploadResult); }}><Eye size={14} className="mr-1" /> Apri Dettaglio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
